import { Reveal } from "@/lib/motion";
import { Check, ArrowRight, Users, BarChart2 } from "lucide-react";
import { ScoreRing } from "@/components/ScoreRing";

const BULLETS = [
  "Get your WebsiteTech Score instantly",
  "Discover critical issues and missed opportunities",
  "Receive a personalized transformation plan",
  "See how your site compares to competitors",
];

export default function AnalyzerFeature() {
  const goAnalyze = () => document.getElementById("analyzer")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section className="relative z-10 py-20 md:py-28 bg-slate-50 border-y border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <div className="bg-white rounded-2xl soft-shadow-lg border border-slate-100 overflow-hidden">
            <div className="flex items-center gap-1.5 px-4 py-3 border-b border-slate-100 bg-slate-50">
              <span className="w-3 h-3 rounded-full bg-red-400" />
              <span className="w-3 h-3 rounded-full bg-amber-400" />
              <span className="w-3 h-3 rounded-full bg-green-400" />
            </div>
            <div className="p-5 grid grid-cols-5 gap-4">
              <div className="col-span-2 bg-[#0B203E] rounded-xl p-4 flex flex-col items-center justify-center">
                <ScoreRing score={68} size={104} stroke={9} showLabel={false} />
                <span className="text-white/60 text-[10px] font-mono mt-2">/100 SCORE</span>
                <div className="w-full mt-4 space-y-2">
                  {[1, 2, 3, 4].map((n) => (
                    <div key={n} className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-md bg-white/10" />
                      <span className="h-1.5 rounded bg-white/10 flex-1" />
                    </div>
                  ))}
                </div>
              </div>
              <div className="col-span-3">
                <p className="font-heading font-bold text-slate-900">Your Website</p>
                <p className="text-xs text-slate-400 font-mono">example.com</p>
                <svg viewBox="0 0 260 90" className="w-full h-24 mt-2" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2563EB" stopOpacity="0.25" />
                      <stop offset="100%" stopColor="#2563EB" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path d="M0,70 C40,40 60,55 90,45 C120,35 150,20 180,30 C210,40 240,15 260,20 L260,90 L0,90 Z" fill="url(#g)" />
                  <path d="M0,70 C40,40 60,55 90,45 C120,35 150,20 180,30 C210,40 240,15 260,20" fill="none" stroke="#2563EB" strokeWidth="2.5" />
                </svg>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className="border border-slate-200 rounded-lg p-3">
                    <Users size={14} className="text-brand" />
                    <p className="font-heading font-bold text-lg text-slate-900 mt-1">21</p>
                    <p className="text-[10px] text-slate-400">Opportunities</p>
                  </div>
                  <div className="border border-slate-200 rounded-lg p-3">
                    <BarChart2 size={14} className="text-green-600" />
                    <p className="font-heading font-bold text-lg text-slate-900 mt-1">1,248 <span className="text-[10px] text-green-600 font-mono">↑18%</span></p>
                    <p className="text-[10px] text-slate-400">Visitors</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <span className="overline">Website Analyzer</span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mt-3">
            See What's Holding Your Website Back
          </h2>
          <p className="text-slate-500 text-lg mt-4 leading-relaxed">
            Our free AI-powered analysis scans 100+ data points across SEO, performance, content,
            security and more.
          </p>
          <ul className="mt-7 space-y-3.5">
            {BULLETS.map((b) => (
              <li key={b} className="flex items-center gap-3">
                <span className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                  <Check size={12} className="text-green-600" strokeWidth={3} />
                </span>
                <span className="text-slate-700 font-medium">{b}</span>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap items-center gap-5 mt-8">
            <button onClick={goAnalyze} data-testid="feature-analyze-btn" className="btn-primary rounded-xl px-7 py-4 flex items-center gap-2">
              Analyze Your Website — It's Free <ArrowRight size={16} />
            </button>
            <span className="font-mono text-sm text-brand">Free. Fast. No credit card required.</span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
